import { useState, useEffect } from 'react';
import clsx from 'clsx';
import useAxios
  from '../../../../../axios/useAxios';
import { getCompanyStoresByCompanyId, getCompanyStoresByStoreId } from '../../../../../actions/storeActions';
import { FaStore } from 'react-icons/fa';
import PrimaryButton from '../../../../../components/button/PrimaryButton';
import { useSelector } from 'react-redux';
import { useFidCardFunnelTracking } from '../../../../../posthog/funnelTrackingFidCard';

export const useStores = (companyId, storeId) => {
  const [company, setCompany] = useState(null);
  const [filteredStores, setFilteredStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const axiosInstance = useAxios();

  const { trackPageViewCreateStore } = useFidCardFunnelTracking();

  useEffect(() => {
    trackPageViewCreateStore({});
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        let data;
        if (companyId) {
          data = await getCompanyStoresByCompanyId(axiosInstance, companyId);
        } else if (storeId) {
          data = await getCompanyStoresByStoreId(axiosInstance, storeId);
        } else {
          setIsLoading(false);
          return;
        }
        setCompany(data);
        setFilteredStores(data.stores);
        setIsLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des magasins:', err);
        setIsLoading(false);
      }
    };
    fetchStores();
  }, [companyId, storeId]);

  const filterStores = (searchTerm) => {
    if (!company?.stores) return;

    if (!searchTerm.trim()) {
      setFilteredStores(company.stores);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    if (typeof searchTermLower !== 'string') return;
    const filtered = company.stores.filter(store =>
      store.name?.toLowerCase()?.includes(searchTermLower) ||
      store.city?.toLowerCase()?.includes(searchTermLower),
    );

    setFilteredStores(filtered);
  };

  return {
    company,
    filteredStores,
    isLoading,
    filterStores,
  };
};

const useExpandableDropdown = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.getElementById('store-search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const expand = () => setIsExpanded(true);
  const collapse = () => setIsExpanded(false);

  return {
    isExpanded,
    expand,
    collapse,
  };
};

const getStoreName = (name) => {
  if (!name) return '';
  const words = name.split(' ');
  return words.length > 1 ? `${words[0]} ${words[1]}` : name;
};

const SearchIcon = ({ isExpanded, selectedStore }) => (
  <svg
    className={clsx(
      'absolute top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400 transition-all duration-300',
      {
        'left-4': isExpanded,
        'right-4': !isExpanded,
        'text-[#596802]': selectedStore && !isExpanded,
      },
    )}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <title>
      {isExpanded
        ? 'Icône de recherche de magasin'
        : 'Icône de sélection de magasin'}
    </title>
    {isExpanded ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
    )}
  </svg>
);

const SearchInput = ({
  searchTerm,
  onSearchChange,
  isExpanded,
  selectedStore,
  onExpand,
}) => {
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onExpand();
    }
  };

  return (
    <button
      type="button"
      className={clsx(
        'relative border-b w-full text-left',
        {
          'border-gray-200': isExpanded,
          'border-transparent': !isExpanded,
        },
      )}
      onClick={onExpand}
      onKeyDown={handleKeyDown}
    >
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={selectedStore ? getStoreName(selectedStore.name) : 'Rechercher un magasin...'}
        className={clsx(
          'w-full h-24 pl-14 pr-4 geomanistMedium text-xl',
          'outline-none transition-all duration-300 focus:bg-[#ECED90]',
          'bg-transparent cursor-pointer',
          {
            'text-[#596802]': selectedStore && !isExpanded,
          },
        )}
        readOnly={!isExpanded}
      />

    </button>
  );
};

const LoadingState = () => (
  <div className="text-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#596802] mx-auto" />
  </div>
);

const ErrorState = () => (
  <div className="text-center py-8 text-[#FF2E6C] geomanistMedium">
    Erreur lors du chargement des magasins
  </div>
);

const EmptyState = () => (
  <div className="text-center py-8 text-gray-500 geomanistMedium">
    Aucun magasin trouvé
  </div>
);

const CompanyHeader = ({ company }) => (
  <div className="flex items-center gap-2 p-4 border-b border-gray-200 bg-gray-50">
    <img
      src={company.image}
      alt={company.name}
      className="w-8 h-8 object-contain"
    />
    <span className="text-lg geomanistBold text-gray-700">
      {company.name}
    </span>
  </div>
);

const StoreItem = ({ store, isSelected, onSelect }) => (
  <button
    key={store.id}
    type="button"
    onClick={() => onSelect(store.id)}
    className={clsx(
      'flex items-center gap-3 p-4 my-2 rounded-xl border transition-all duration-200 w-full',
      {
        'bg-[#ECED90] border-[#596802]': isSelected,
        'bg-white border-gray-200 hover:bg-gray-50': !isSelected,
      },
    )}
  >
    <div className="flex-1 text-left">
      <div className="text-lg geomanistMedium">
        {getStoreName(store.name)}
      </div>
      <div className="text-sm text-gray-500">
        {store.city || ''}
      </div>
    </div>
    {isSelected && (
      <svg className="w-6 h-6 text-[#596802]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <title>Icône de sélection de magasin</title>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
      </svg>
    )}
  </button>
);

const StoreList = ({ company, filteredStores, selectedStoreId, onStoreSelect }) => (
  <div className="flex flex-col">
    <CompanyHeader company={company} />
    <div className="p-4 overflow-y-auto overflow-x-hidden">
      {filteredStores.map((store) => (
        <StoreItem
          key={store.id}
          store={store}
          isSelected={selectedStoreId === store.id}
          onSelect={onStoreSelect}
        />
      ))}
    </div>
  </div>
);

const DropdownContent = ({
  isExpanded,
  isLoading,
  company,
  filteredStores,
  selectedStoreId,
  onStoreSelect,
}) => {
  if (!isExpanded) return null;

  if (isLoading) return <LoadingState />;

  if (!company) return <ErrorState />;

  if (filteredStores.length === 0) return <EmptyState />;

  return (
    <StoreList
      company={company}
      filteredStores={filteredStores}
      selectedStoreId={selectedStoreId}
      onStoreSelect={onStoreSelect}
    />
  );
};

const ErrorDisplay = ({ error }) => {
  if (!error) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-[#FFF0F5] rounded-lg border border-[#FF2E6C]/30">
      <svg className="w-4 h-4 text-[#FF2E6C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <title>Erreur</title>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-[#FF2E6C] text-xl geomanistMedium">{error}</span>
    </div>
  );
};

const CompletionButton = ({ selectedStore, isExpanded, onComplete, isLoading }) => {
  if (!selectedStore || isExpanded) return null;

  return (
    <div className="w-full mt-auto">
      <PrimaryButton
        onClick={onComplete}
        label="Terminer"
        className="w-full appear"
        isLoading={isLoading}
      />
    </div>
  );
};

const StoreSearchStep = ({ value, onChange, error, onComplete, isButtonLoading, companyId, storeId, viewNextButton = true }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { company, filteredStores, isLoading, filterStores } = useStores(companyId, storeId);
  const { isExpanded, expand, collapse } = useExpandableDropdown();

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    filterStores(searchValue);
  };

  const handleStoreSelect = (companyId) => {
    onChange(companyId);
    collapse();
  };

  const selectedStore = company?.stores?.find(store => store.id === value);

  return (
    <div className="flex flex-col w-full h-full appear gap-6">
      <div
        id="store-search-container"
        className={clsx(
          'bg-white !rounded-2xl',
          'overflow-hidden transition-all duration-300',
          {
            'max-h-[600px] border border-gray-200': isExpanded,
            'max-h-24': !isExpanded,
          },
        )}
      >
        <SearchInput
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          isExpanded={isExpanded}
          selectedStore={selectedStore}
          onExpand={expand}
        />

        <div className="overflow-y-auto">
          <DropdownContent
            isExpanded={isExpanded}
            isLoading={isLoading}
            company={company}
            filteredStores={filteredStores}
            selectedStoreId={value}
            onStoreSelect={handleStoreSelect}
          />
        </div>
      </div>

      <ErrorDisplay error={error} />

      {viewNextButton && (
        <CompletionButton
          selectedStore={selectedStore}
          isExpanded={isExpanded}
          onComplete={onComplete}
          isLoading={isButtonLoading}
        />
      )}
    </div>
  );
};

export default StoreSearchStep;
